import {
  Certificate,
  getUserCertificates,
  isValidSystemSetup,
} from "crypto-pro";
import { ReactElement, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { LoadingPage } from "./components/LoadingPage/LoadingPage";
import { IerrorCertificate } from "./interfaces/error/IerrorCertificate";
import { AppRoutes } from "./routes/AppRoutes";
import { useAppDispatch } from "./store/reduxHooks";
import {
  UP_DATE_APPLICATIONS_ALL,
  UP_DATE_APPLICATIONS_TYPE_BG,
} from "./store/slice/applicationsSlice/applicationsSlice";
import {
  CSP_CERTIFICATE,
  LOADING_PAGE,
  TypeInitialState,
  UP_DATE_BANK_STATUSES,
  dataResult,
} from "./store/slice/authSliceLogin/authSliceLogin";
import { UP_DATE_BANKS_STORE } from "./store/slice/banksSlice/banksSlice";
import { UP_DATE_LIST_AGENT_STORE } from "./store/slice/listAgentSlice/listAgentSlice";
import {
  IOrganization,
  UP_DATE_LIST_CLIENT_FOR_AGENT,
} from "./store/slice/listClientForAgentSlice/listClientForAgentSlice";
import {
  IClient,
  UP_DATE_LIST_CLIENT_STORE,
} from "./store/slice/listClientSlice/listClientSlice";
import { UP_DATE_LIST_MANAGER } from "./store/slice/listManagerSlice/listManagerSlice";
import { UP_DATE_NOTIFICATIONS_ALL } from "./store/slice/notificationsSlice/notificationsSlice";
import { UP_DATE_PROFILE } from "./store/slice/profileSlice/profileSlice";
import { UP_DATE_ROUTING } from "./store/slice/routingSlice/routingSlice";
import { USER } from "./types/indexTypeEnum";
import { PageLayoutLogin, PageUnsubscribe } from "./views/indexPage";
import { PageAgreementDataProcessingDocument } from "./views/pageAgreementDocument/PageAgreementDataProcessingDocument";
import { PageDocumentsSigning } from "./views/pageDocumentsSigning/PageDocumentsSigning";
import { PageLogin } from "./views/pageLogin/PageLogin";
import { PagePrivacyPolicyDocument } from "./views/pagePrivacyPolicyDocument/pagePrivacyPolicyDocument";
import { PageRecoveryPassword } from "./views/pageRecoveryPassword/pageRecoveryPassword";
import { PageRegisterAgent } from "./views/pageRegisterAgent/pageRegisterAgent";
import { PageRegisterClient } from "./views/pageRegisterClient/pageRegisterClient";
import { PageSelectRegistration } from "./views/pageSelectRegistration/pageSelectRegistration";

export function App(): ReactElement {
  const isAuthenticated = useSelector(
    (state: { authSlice: TypeInitialState }) => state.authSlice,
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const takeAllCertificates = useCallback(() => {
    if (
      isAuthenticated.LoadingPage === "LOGIN" ||
      isAuthenticated.LoadingPage === "PREV_COMPLICATED"
    ) {
      isValidSystemSetup().then(async (isValid) => {
        if (isValid) {
          try {
            const CSP: Certificate[] | IerrorCertificate =
              await getUserCertificates();
            const CSP_ARRY: dataResult[] = await Promise.all(
              CSP?.map(async (Cert) => {
                let object: dataResult = {};
                const Cert_Data = await Cert.getOwnerInfo();
                Cert_Data.forEach((Data) => {
                  if (Data.title === "Владелец") {
                    object = { ...object, owner: Data.description };
                  }
                  if (Data.title === "ИНН ЮЛ") {
                    object = { ...object, inn_ul: Data.description };
                  } else if (Data.title === "ИНН") {
                    object = { ...object, inn_ul: Data.description };
                  }
                });
                object = { ...object, thumbprint: Cert.thumbprint };

                return object;
              }),
            );

            dispatch(
              CSP_CERTIFICATE({
                CSP_FILTER: CSP_ARRY,
              }),
            );
          } catch (error) {
            console.error(error);
          }
        }
      });
    }
  }, [dispatch, isAuthenticated.LoadingPage]);

  // переключение страниц при входе
  useEffect(() => {
    if (isAuthenticated.LoadingPage === "LOGIN") {
      takeAllCertificates();
      dispatch(LOADING_PAGE("LOGIN"));
    }
    if (isAuthenticated.LoadingPage === "PREV_COMPLICATED") {
      const timer = setTimeout(() => {
        takeAllCertificates();
        dispatch(LOADING_PAGE("COMPLICATED"));
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [dispatch, isAuthenticated.LoadingPage, navigate, takeAllCertificates]);

  // запись данных с permission
  useEffect(() => {
    if (isAuthenticated.LoadingPage === "COMPLICATED") {
      if (isAuthenticated.data) {
        Object.keys(isAuthenticated.data).map((data_item) => {
          if (data_item === "routes") {
            const ROUTES = isAuthenticated.data[`${data_item}`];
            if (ROUTES !== undefined) {
              dispatch(UP_DATE_ROUTING(ROUTES));
            }
          }
          if (data_item === "user") {
            const PROFILE = isAuthenticated.data[`${data_item}`];
            if (PROFILE !== undefined) {
              dispatch(UP_DATE_PROFILE(PROFILE));
            }
          }
          if (data_item === "banks") {
            const BANKS = isAuthenticated.data[`${data_item}`];
            if (BANKS !== undefined) {
              dispatch(UP_DATE_BANKS_STORE(BANKS));
            }
          }
          if (data_item === "applications") {
            const APPLICATIONS = isAuthenticated.data[`${data_item}`];
            if (APPLICATIONS !== undefined) {
              dispatch(UP_DATE_APPLICATIONS_ALL(APPLICATIONS));
            }
          }
          if (data_item === "agents") {
            const LIST_AGENT = isAuthenticated.data[`${data_item}`];
            if (LIST_AGENT !== undefined) {
              dispatch(UP_DATE_LIST_AGENT_STORE(LIST_AGENT));
            }
          }
          if (data_item === "clients") {
            const LIST_CLIENT = isAuthenticated.data[`${data_item}`];
            if (LIST_CLIENT !== undefined) {
              if (isAuthenticated.data[`user`]?.type === USER.AGENT_SIMPLE) {
                dispatch(
                  UP_DATE_LIST_CLIENT_FOR_AGENT(LIST_CLIENT as IOrganization[]),
                );
              } else {
                dispatch(UP_DATE_LIST_CLIENT_STORE(LIST_CLIENT as IClient[]));
              }
            }
          }
          if (data_item === "type_bg_list") {
            const LIST_TYPE_BG = isAuthenticated.data[`${data_item}`];
            if (LIST_TYPE_BG !== undefined) {
              dispatch(UP_DATE_APPLICATIONS_TYPE_BG(LIST_TYPE_BG));
            }
          }
          if (data_item === "bank_statuses") {
            const BANK_STATUSES = isAuthenticated.data[`${data_item}`];
            if (BANK_STATUSES !== undefined) {
              dispatch(UP_DATE_BANK_STATUSES(BANK_STATUSES));
            }
          }
          if (data_item === "alerts") {
            const ALERTS = isAuthenticated.data[`${data_item}`];
            if (ALERTS !== undefined) {
              dispatch(UP_DATE_NOTIFICATIONS_ALL(ALERTS));
            }
          }
          if (data_item === "manager") {
            const MANAGER = isAuthenticated.data[`${data_item}`];
            if (MANAGER !== undefined) {
              dispatch(UP_DATE_LIST_MANAGER(MANAGER));
            }
          }
        });
      }
    }
  }, [dispatch, isAuthenticated.LoadingPage, isAuthenticated.data]);

  if (isAuthenticated.LoadingPage === "COMPLICATED") {
    return <AppRoutes />;
  }
  if (
    isAuthenticated.LoadingPage === "LOADING" ||
    isAuthenticated.LoadingPage === "PREV_COMPLICATED"
  ) {
    return <LoadingPage />;
  }
  return (
    <Routes>
      <Route path="/" element={<PageLayoutLogin />}>
        <Route path="/login" element={<PageLogin />} />
        <Route path="/registerAgent" element={<PageRegisterAgent />} />
        <Route path="/registerClient" element={<PageRegisterClient />} />
        <Route path="/recoveryPassword" element={<PageRecoveryPassword />} />
        <Route
          path="/selectRegistration"
          element={<PageSelectRegistration />}
        />
        <Route
          path="/agreementDataProcessing"
          element={<PageAgreementDataProcessingDocument />}
        />
        <Route
          path="/agreementPrivacyPolicy"
          element={<PagePrivacyPolicyDocument />}
        />
        <Route index element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Route>
      <Route path={"/documents-signing"} element={<PageDocumentsSigning />} />
      <Route path="/unsubscribe/:token" element={<PageUnsubscribe />} />
    </Routes>
  );
}
